import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  ClipboardList,
  Download,
  FileSignature,
  FileUp,
  Loader2,
  PlayCircle,
  RefreshCcw,
  Upload,
} from "lucide-react";

import { PageHeader } from "@/components/shared/PageHeader";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  buildInforme,
  downloadInformeXml,
  getAutomationConfig,
  getSolucion,
  ingestBaseline,
  listEvents,
  listInformes,
  markInformeResult,
  signInforme,
  updateAutomationConfig,
  updateSolucionFechaDesde,
  uploadInformeToDgi,
  type InformeStatus,
  type ProveedorSoftwareAutomationConfigUpdate,
  type ProveedorSoftwareInforme,
} from "@/services/proveedorSoftwareService";
import { formatDisplayDate } from "@/lib/dateFormat";

const QK = {
  solucion: ["proveedor-software", "solucion"],
  events: ["proveedor-software", "events"],
  informes: ["proveedor-software", "informes"],
  automation: ["proveedor-software", "automation"],
};

function suggestNextPeriodo(): string {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function statusVariant(
  status: InformeStatus,
): "default" | "secondary" | "destructive" | "outline" {
  if (status === "accepted") return "default";
  if (status === "rejected" || status === "failed") return "destructive";
  if (status === "signed" || status === "submitted") return "secondary";
  return "outline";
}

export function ProveedorSoftwarePage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const solucionQuery = useQuery({ queryKey: QK.solucion, queryFn: () => getSolucion() });
  const eventsQuery = useQuery({ queryKey: QK.events, queryFn: () => listEvents() });
  const informesQuery = useQuery({ queryKey: QK.informes, queryFn: () => listInformes() });
  const automationQuery = useQuery({
    queryKey: QK.automation,
    queryFn: () => getAutomationConfig(),
  });

  const [fechaDesdeDraft, setFechaDesdeDraft] = useState<string>("");
  const [buildPeriodo, setBuildPeriodo] = useState<string>(suggestNextPeriodo());
  const [buildEnv, setBuildEnv] = useState<"test" | "prod">("test");

  // Track in-flight automation form values locally so the user can toggle
  // several fields before pressing Save.
  const [auto, setAuto] = useState<ProveedorSoftwareAutomationConfigUpdate>({});
  const automationConfig = automationQuery.data;
  const effectiveAuto = {
    enabled: auto.enabled ?? automationConfig?.enabled ?? false,
    cadence: auto.cadence ?? automationConfig?.cadence ?? "manual",
    dayOfMonth: auto.dayOfMonth ?? automationConfig?.dayOfMonth ?? 25,
    environment: auto.environment ?? automationConfig?.environment ?? "test",
    autoSubmit: auto.autoSubmit ?? automationConfig?.autoSubmit ?? false,
  };

  const updateFechaDesde = useMutation({
    mutationFn: (value: string) => updateSolucionFechaDesde(value),
    onSuccess: () => {
      toast.success(t("proveedorSoftware.toast.fechaDesdeSaved", "Fecha desde updated"));
      queryClient.invalidateQueries({ queryKey: QK.solucion });
      setFechaDesdeDraft("");
    },
    onError: () =>
      toast.error(t("proveedorSoftware.toast.fechaDesdeError", "Could not save fecha desde")),
  });

  const ingest = useMutation({
    mutationFn: (file: File) => ingestBaseline(file),
    onSuccess: (res) => {
      toast.success(
        t("proveedorSoftware.toast.baselineDone", {
          defaultValue: "Inserted {{inserted}}, skipped {{skipped}}, errors {{errors}}",
          inserted: res.inserted,
          skipped: res.skipped,
          errors: res.errors.length,
        }),
      );
      queryClient.invalidateQueries({ queryKey: QK.events });
      if (res.errors.length > 0) {
        // eslint-disable-next-line no-console
        console.warn("Baseline ingest errors:", res.errors);
      }
    },
    onError: () =>
      toast.error(t("proveedorSoftware.toast.baselineError", "Baseline ingest failed")),
  });

  const build = useMutation({
    mutationFn: () => buildInforme(buildPeriodo, buildEnv),
    onSuccess: (res) => {
      toast.success(
        t("proveedorSoftware.toast.builtOk", {
          defaultValue: "Built informe {{periodo}}#{{secuencia}} ({{rucs}} clientes, {{sols}} solución)",
          periodo: res.periodo,
          secuencia: res.secuencia,
          rucs: res.cantRucs,
          sols: res.cantSolSoft,
        }),
      );
      queryClient.invalidateQueries({ queryKey: QK.informes });
      queryClient.invalidateQueries({ queryKey: QK.events });
    },
    onError: (err: unknown) =>
      toast.error(
        err instanceof Error
          ? err.message
          : t("proveedorSoftware.toast.buildError", "Build failed"),
      ),
  });

  const sign = useMutation({
    mutationFn: (informeId: string) => signInforme(informeId),
    onSuccess: () => {
      toast.success(t("proveedorSoftware.toast.signed", "Informe signed"));
      queryClient.invalidateQueries({ queryKey: QK.informes });
    },
    onError: (err: unknown) =>
      toast.error(
        err instanceof Error
          ? err.message
          : t("proveedorSoftware.toast.signError", "Sign failed"),
      ),
  });

  const [markTarget, setMarkTarget] =
    useState<ProveedorSoftwareInforme | null>(null);

  const upload = useMutation({
    mutationFn: (informeId: string) => uploadInformeToDgi(informeId),
    onSuccess: (res) => {
      if (res.accepted) {
        toast.success(
          "Subida iniciada en DGI. El resultado va a aparecer en unos minutos.",
        );
      } else {
        toast.error(
          res.message
            ? `DGI no aceptó la subida: ${res.message}`
            : "DGI no aceptó la subida.",
        );
      }
      queryClient.invalidateQueries({ queryKey: QK.informes });
    },
    onError: (err: unknown) =>
      toast.error(
        err instanceof Error
          ? err.message
          : "No pudimos iniciar la subida a DGI.",
      ),
  });

  const saveAutomation = useMutation({
    mutationFn: (patch: ProveedorSoftwareAutomationConfigUpdate) =>
      updateAutomationConfig(patch),
    onSuccess: () => {
      toast.success(
        t("proveedorSoftware.toast.automationSaved", "Automation config saved"),
      );
      setAuto({});
      queryClient.invalidateQueries({ queryKey: QK.automation });
    },
    onError: () =>
      toast.error(
        t("proveedorSoftware.toast.automationError", "Could not save automation"),
      ),
  });

  if (
    solucionQuery.isLoading ||
    eventsQuery.isLoading ||
    informesQuery.isLoading ||
    automationQuery.isLoading
  ) {
    return <LoadingLogo />;
  }

  const sol = solucionQuery.data;
  const events = eventsQuery.data;
  const informes = informesQuery.data ?? [];

  return (
    <div className="space-y-6 fx-content">
      <PageHeader
        title={t("proveedorSoftware.title", "Proveedor de Software")}
        description={t(
          "proveedorSoftware.description",
          "Informe Proveedores de Software (Res. DGI 167/021) — generación, firma y configuración de automatización.",
        )}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t("proveedorSoftware.solucion.title", "Solución registrada")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {sol ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Código Producto" value={sol.codigoProducto} />
              <Field label="Código Solución" value={String(sol.codigoSolucion)} />
              <Field label="Nombre" value={sol.nombreSw} />
              <Field
                label="Procesos"
                value={sol.procesos.length > 0 ? sol.procesos.join(", ") : "—"}
              />
              <Field label="Fecha desde" value={formatDisplayDate(sol.fechaDesde)} />
              <Field
                label="Fecha hasta"
                value={sol.fechaHasta ? formatDisplayDate(sol.fechaHasta) : "—"}
              />
            </div>
          ) : null}
          <Separator />
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex-1 max-w-xs">
              <Label htmlFor="fechaDesde">
                {t(
                  "proveedorSoftware.solucion.editFechaDesde",
                  "Corregir Fecha desde (YYYY-MM-DD)",
                )}
              </Label>
              <Input
                id="fechaDesde"
                type="date"
                value={fechaDesdeDraft || sol?.fechaDesde || ""}
                onChange={(e) => setFechaDesdeDraft(e.target.value)}
              />
            </div>
            <Button
              onClick={() => fechaDesdeDraft && updateFechaDesde.mutate(fechaDesdeDraft)}
              disabled={!fechaDesdeDraft || updateFechaDesde.isPending}
            >
              {updateFechaDesde.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("common.save", "Save")
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("proveedorSoftware.baseline.title", "Baseline (CSV)")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {t(
              "proveedorSoftware.baseline.description",
              "Importa los clientes que ya estaban en producción antes de capturar eventos. Idempotente: si ya existe un cliente_alta para el tenant, se omite. Columnas: rut, fechaInicio, fechaFin?, notes?.",
            )}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) ingest.mutate(file);
              e.target.value = "";
            }}
          />
          <Button
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={ingest.isPending}
          >
            {ingest.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileUp className="h-4 w-4" />
            )}
            <span className="ml-2">{t("proveedorSoftware.baseline.upload", "Subir CSV")}</span>
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-2">
          <CardTitle>{t("proveedorSoftware.events.title", "Eventos por período")}</CardTitle>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => queryClient.invalidateQueries({ queryKey: QK.events })}
            aria-label={t("common.refresh", "Refresh")}
          >
            <RefreshCcw className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {events && Object.keys(events.byPeriodo).length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {Object.entries(events.byPeriodo)
                .sort(([a], [b]) => (a < b ? -1 : 1))
                .map(([periodo, count]) => (
                  <Badge key={periodo} variant="outline">
                    {periodo}: {count}
                  </Badge>
                ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t("proveedorSoftware.events.empty", "Aún no hay eventos registrados.")}
            </p>
          )}
          {events && events.data.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>RUC cliente</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Período</TableHead>
                  <TableHead>Informe</TableHead>
                  <TableHead>Notas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.data.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="text-xs">{e.type}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {e.rucCliente ?? "—"}
                    </TableCell>
                    <TableCell>{formatDisplayDate(e.fechaEvento)}</TableCell>
                    <TableCell>{e.periodo}</TableCell>
                    <TableCell className="text-xs">
                      {e.informeId ? (
                        <span className="font-mono">{e.informeId.slice(0, 8)}</span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{e.notes ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("proveedorSoftware.informes.title", "Informes")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex-1 max-w-xs">
              <Label htmlFor="periodo">{t("proveedorSoftware.informes.periodo", "Período (AAAAMM)")}</Label>
              <Input
                id="periodo"
                inputMode="numeric"
                maxLength={6}
                value={buildPeriodo}
                onChange={(e) => setBuildPeriodo(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="202605"
              />
            </div>
            <div className="w-full sm:w-40">
              <Label>{t("proveedorSoftware.informes.environment", "Entorno")}</Label>
              <Select value={buildEnv} onValueChange={(v) => setBuildEnv(v as "test" | "prod")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="test">Homologación (test)</SelectItem>
                  <SelectItem value="prod">Producción</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => build.mutate()}
              disabled={!/^\d{6}$/.test(buildPeriodo) || build.isPending}
            >
              {build.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <PlayCircle className="h-4 w-4" />
              )}
              <span className="ml-2">{t("proveedorSoftware.informes.build", "Generar XML")}</span>
            </Button>
          </div>

          {informes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("proveedorSoftware.informes.empty", "Aún no se ha generado ningún informe.")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Período</TableHead>
                  <TableHead>Sec.</TableHead>
                  <TableHead>Entorno</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Sol.</TableHead>
                  <TableHead>RUCs</TableHead>
                  <TableHead>Generado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {informes.map((i) => {
                  // 'signed' is re-signable too — the backend signer only
                  // refuses 'submitted' / 'accepted' (those are already in
                  // DGI's hands and re-signing would break the secuencia
                  // audit). Re-signing covers the case where the signer
                  // shape changed (e.g. a DGI rejection on the previous
                  // shape) and the informe needs to be regenerated.
                  const canSign =
                    i.status === "generated" ||
                    i.status === "failed" ||
                    i.status === "signed";
                  const signLabel =
                    i.status === "signed" ? "Volver a firmar" : "Firmar";
                  const hasUnsigned = i.status !== "pending";
                  const hasSigned =
                    i.status === "signed" ||
                    i.status === "submitted" ||
                    i.status === "accepted";
                  return (
                    <TableRow key={i.id}>
                      <TableCell>{i.periodo}</TableCell>
                      <TableCell>{i.secuencia}</TableCell>
                      <TableCell className="text-xs uppercase">{i.environment}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 items-start">
                          <Badge variant={statusVariant(i.status)}>{i.status}</Badge>
                          {i.dgiEnvioNumero ? (
                            <span className="text-[10px] text-muted-foreground font-mono">
                              DGI envío #{i.dgiEnvioNumero}
                            </span>
                          ) : null}
                          {i.failureCode || i.failureReason ? (
                            <span className="text-[10px] text-destructive">
                              {i.failureReason ?? i.failureCode}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>{i.cantSolSoft}</TableCell>
                      <TableCell>{i.cantRucs}</TableCell>
                      <TableCell className="text-xs">
                        {i.generatedAt ? formatDisplayDate(i.generatedAt) : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canSign ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => sign.mutate(i.id)}
                              disabled={sign.isPending}
                            >
                              <FileSignature className="h-3.5 w-3.5" />
                              <span className="ml-1">{signLabel}</span>
                            </Button>
                          ) : null}
                          {hasUnsigned ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                void downloadInformeXml(i.id, false).catch(
                                  () =>
                                    toast.error(
                                      "No pudimos descargar el XML.",
                                    ),
                                )
                              }
                            >
                              <Download className="h-3.5 w-3.5" />
                              <span className="ml-1">XML</span>
                            </Button>
                          ) : null}
                          {hasSigned ? (
                            <Button
                              size="sm"
                              onClick={() =>
                                void downloadInformeXml(i.id, true).catch(
                                  () =>
                                    toast.error(
                                      "No pudimos descargar el XML firmado.",
                                    ),
                                )
                              }
                            >
                              <Download className="h-3.5 w-3.5" />
                              <span className="ml-1">XML firmado</span>
                            </Button>
                          ) : null}
                          {/* Auto-upload via the Python eFactura flow.
                              Hidden on submitted / terminal rows. */}
                          {i.status === "signed" ? (
                            <Button
                              size="sm"
                              onClick={() => upload.mutate(i.id)}
                              disabled={upload.isPending}
                            >
                              {upload.isPending &&
                              upload.variables === i.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Upload className="h-3.5 w-3.5" />
                              )}
                              <span className="ml-1">Subir a DGI</span>
                            </Button>
                          ) : null}
                          {/* Manual mark-as-recibido / rechazado fallback —
                              available while a row is signed or in flight
                              so a human can record the result if the
                              automation fails to classify. Hidden once
                              the row reaches a terminal DGI state. */}
                          {i.status === "signed" || i.status === "submitted" ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setMarkTarget(i)}
                            >
                              <ClipboardList className="h-3.5 w-3.5" />
                              <span className="ml-1">Registrar manualmente</span>
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("proveedorSoftware.automation.title", "Automatización")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t(
              "proveedorSoftware.automation.description",
              "Configuración de la generación + firma automática mensual. Aún no está conectada al cron — solo persiste la intención.",
            )}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-center gap-3">
              <Checkbox
                id="autoEnabled"
                checked={effectiveAuto.enabled}
                onCheckedChange={(v) => setAuto((s) => ({ ...s, enabled: Boolean(v) }))}
              />
              <Label htmlFor="autoEnabled" className="cursor-pointer">
                {t("proveedorSoftware.automation.enabled", "Habilitado")}
              </Label>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox
                id="autoSubmit"
                checked={effectiveAuto.autoSubmit}
                onCheckedChange={(v) => setAuto((s) => ({ ...s, autoSubmit: Boolean(v) }))}
              />
              <Label htmlFor="autoSubmit" className="cursor-pointer">
                {t("proveedorSoftware.automation.autoSubmit", "Subir al portal DGI al firmar")}
              </Label>
            </div>
            <div>
              <Label>{t("proveedorSoftware.automation.cadence", "Cadencia")}</Label>
              <Select
                value={effectiveAuto.cadence}
                onValueChange={(v) =>
                  setAuto((s) => ({ ...s, cadence: v as "manual" | "monthly" }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="monthly">Mensual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="dayOfMonth">
                {t("proveedorSoftware.automation.dayOfMonth", "Día del mes (1-28)")}
              </Label>
              <Input
                id="dayOfMonth"
                type="number"
                min={1}
                max={28}
                value={effectiveAuto.dayOfMonth}
                onChange={(e) =>
                  setAuto((s) => ({ ...s, dayOfMonth: Number(e.target.value) }))
                }
              />
            </div>
            <div>
              <Label>{t("proveedorSoftware.automation.environment", "Entorno objetivo")}</Label>
              <Select
                value={effectiveAuto.environment}
                onValueChange={(v) =>
                  setAuto((s) => ({ ...s, environment: v as "test" | "prod" }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="test">Homologación</SelectItem>
                  <SelectItem value="prod">Producción</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {automationConfig?.lastRunAt ? (
            <p className="text-xs text-muted-foreground">
              {t("proveedorSoftware.automation.lastRun", "Última ejecución")}: {formatDisplayDate(automationConfig.lastRunAt)}
            </p>
          ) : null}
          <Button
            onClick={() => saveAutomation.mutate(auto)}
            disabled={Object.keys(auto).length === 0 || saveAutomation.isPending}
          >
            {saveAutomation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t("common.save", "Save")
            )}
          </Button>
        </CardContent>
      </Card>

      <MarkResultDialog
        target={markTarget}
        onClose={() => setMarkTarget(null)}
        onSaved={() => {
          setMarkTarget(null);
          queryClient.invalidateQueries({ queryKey: QK.informes });
        }}
      />
    </div>
  );
}

function MarkResultDialog({
  target,
  onClose,
  onSaved,
}: {
  target: ProveedorSoftwareInforme | null;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const open = target != null;
  const [outcome, setOutcome] = useState<"accepted" | "rejected">("accepted");
  const [envioNumero, setEnvioNumero] = useState("");
  const [motivoCode, setMotivoCode] = useState("I03");
  const [motivoMensaje, setMotivoMensaje] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset on open for a different target.
  useEffect(() => {
    if (!open) return;
    setOutcome("accepted");
    setEnvioNumero("");
    setMotivoCode("I03");
    setMotivoMensaje("");
    setSaving(false);
  }, [open, target?.id]);

  const handleSubmit = async (): Promise<void> => {
    if (!target || saving) return;
    if (envioNumero.trim() === "") {
      toast.error("Ingresá el número de envío que devolvió DGI.");
      return;
    }
    if (outcome === "rejected" && !/^I\d{2}$/.test(motivoCode.trim())) {
      toast.error("El código de motivo debe tener el formato I01..I09.");
      return;
    }
    setSaving(true);
    try {
      await markInformeResult(target.id, {
        outcome,
        envioNumero: envioNumero.trim(),
        motivoCode: outcome === "rejected" ? motivoCode.trim() : undefined,
        motivoMensaje:
          outcome === "rejected" && motivoMensaje.trim() !== ""
            ? motivoMensaje.trim()
            : undefined,
      });
      toast.success(
        outcome === "accepted"
          ? "Informe marcado como Recibido."
          : "Informe marcado como Rechazado.",
      );
      onSaved();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "No pudimos registrar el resultado.",
      );
    } finally {
      setSaving(false);
    }
  };

  // I01..I09 motivo codes from Anexo 3 — `docs/dgi/Formato_Mensaje_de_Respuesta_v19.md`.
  const MOTIVO_CODES: Array<{ code: string; label: string }> = [
    { code: "I01", label: "I01 — Formato del archivo no es el indicado" },
    { code: "I02", label: "I02 — No coincide RUC en Informe, Certificado o envío" },
    { code: "I03", label: "I03 — Firma o Certificado electrónicos no son válidos" },
    { code: "I04", label: "I04 — No cumple validaciones del Formato de Informe" },
    { code: "I05", label: "I05 — La secuencia indicada en el informe no es correcta" },
    { code: "I06", label: "I06 — Fecha recepción menor a periodo informado" },
    { code: "I07", label: "I07 — No se aceptó la Declaración de cumplimiento" },
    { code: "I09", label: "I09 — Datos del nombre del archivo no coinciden con el contenido" },
  ];

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Registrar resultado DGI</DialogTitle>
          <DialogDescription>
            {target ? (
              <>
                Período <strong>{target.periodo}</strong> · secuencia{" "}
                <strong>{target.secuencia}</strong> · entorno{" "}
                <strong>{target.environment.toUpperCase()}</strong>.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <Button
              type="button"
              variant={outcome === "accepted" ? "default" : "outline"}
              onClick={() => setOutcome("accepted")}
              className="flex-1"
            >
              Recibido
            </Button>
            <Button
              type="button"
              variant={outcome === "rejected" ? "destructive" : "outline"}
              onClick={() => setOutcome("rejected")}
              className="flex-1"
            >
              Rechazado
            </Button>
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="envio-numero">Número de envío (DGI)</Label>
            <Input
              id="envio-numero"
              inputMode="numeric"
              placeholder="11398"
              value={envioNumero}
              onChange={(e) => setEnvioNumero(e.currentTarget.value)}
            />
            <p className="text-xs text-muted-foreground">
              El número que aparece en el mensaje del portal: "Su envío número 0 al cual se le asignó el número <strong>11398</strong>…".
            </p>
          </div>

          {outcome === "rejected" ? (
            <>
              <div className="flex flex-col gap-1">
                <Label htmlFor="motivo-code">Motivo de rechazo</Label>
                <Select value={motivoCode} onValueChange={setMotivoCode}>
                  <SelectTrigger id="motivo-code">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MOTIVO_CODES.map((m) => (
                      <SelectItem key={m.code} value={m.code}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="motivo-mensaje">Mensaje (opcional)</Label>
                <Input
                  id="motivo-mensaje"
                  placeholder="Texto adicional del portal"
                  value={motivoMensaje}
                  onChange={(e) => setMotivoMensaje(e.currentTarget.value)}
                />
              </div>
            </>
          ) : null}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving}
            variant={outcome === "rejected" ? "destructive" : "default"}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <p className="text-xs uppercase text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  );
}
